import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Grid, Avatar,
  TextField, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, LinearProgress, useMediaQuery,
  useTheme, Tabs, Tab, IconButton, Menu, Divider, Select, FormControl,
  InputLabel
} from '@mui/material';
import {
  TrendingUp, TrendingDown, EmojiEvents, FilterList, Person,
  LocationOn, CalendarToday, Download, Refresh
} from '@mui/icons-material';

const StaffPerformance = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [filterAnchor, setFilterAnchor] = useState(null);

  // Demo Data - Branches
  const branches = [
    { id: 'all', name: 'All Branches', region: 'all' },
    { id: 'BLR001', name: 'Bangalore - MG Road', region: 'south' },
    { id: 'BLR002', name: 'Bangalore - Koramangala', region: 'south' },
    { id: 'MUM001', name: 'Mumbai - Andheri', region: 'west' },
    { id: 'MUM002', name: 'Mumbai - Bandra', region: 'west' },
    { id: 'DEL001', name: 'Delhi - Connaught Place', region: 'north' },
    { id: 'DEL002', name: 'Delhi - Dwarka', region: 'north' }
  ];

  const regions = [
    { id: 'all', name: 'All Regions' },
    { id: 'south', name: 'South Region' },
    { id: 'west', name: 'West Region' },
    { id: 'north', name: 'North Region' }
  ];

  // Demo Data - Staff Performance
  const staffPerformance = [
    {
      id: 'EMP001',
      name: 'Rajesh Kumar',
      branch: 'BLR001',
      branchName: 'Bangalore - MG Road',
      region: 'south',
      target: 500000,
      collected: 525000,
      achievement: 105,
      customers: 45,
      collections: 156,
      avatar: 'RK',
      rank: 1
    },
    {
      id: 'EMP002',
      name: 'Priya Sharma',
      branch: 'BLR001',
      branchName: 'Bangalore - MG Road',
      region: 'south',
      target: 450000,
      collected: 465000,
      achievement: 103.3,
      customers: 42,
      collections: 148,
      avatar: 'PS',
      rank: 2
    },
    {
      id: 'EMP003',
      name: 'Amit Patel',
      branch: 'MUM001',
      branchName: 'Mumbai - Andheri',
      region: 'west',
      target: 480000,
      collected: 485000,
      achievement: 101,
      customers: 38,
      collections: 142,
      avatar: 'AP',
      rank: 3
    },
    {
      id: 'EMP004',
      name: 'Sunita Reddy',
      branch: 'BLR002',
      branchName: 'Bangalore - Koramangala',
      region: 'south',
      target: 420000,
      collected: 418000,
      achievement: 99.5,
      customers: 40,
      collections: 135,
      avatar: 'SR',
      rank: 4
    },
    {
      id: 'EMP005',
      name: 'Vikram Singh',
      branch: 'DEL001',
      branchName: 'Delhi - Connaught Place',
      region: 'north',
      target: 510000,
      collected: 502000,
      achievement: 98.4,
      customers: 44,
      collections: 158,
      avatar: 'VS',
      rank: 5
    },
    {
      id: 'EMP006',
      name: 'Meera Iyer',
      branch: 'BLR002',
      branchName: 'Bangalore - Koramangala',
      region: 'south',
      target: 400000,
      collected: 390000,
      achievement: 97.5,
      customers: 35,
      collections: 128,
      avatar: 'MI',
      rank: 6
    },
    {
      id: 'EMP007',
      name: 'Arjun Rao',
      branch: 'MUM002',
      branchName: 'Mumbai - Bandra',
      region: 'west',
      target: 460000,
      collected: 445000,
      achievement: 96.7,
      customers: 39,
      collections: 140,
      avatar: 'AR',
      rank: 7
    },
    {
      id: 'EMP008',
      name: 'Kavita Deshmukh',
      branch: 'DEL002',
      branchName: 'Delhi - Dwarka',
      region: 'north',
      target: 430000,
      collected: 410000,
      achievement: 95.3,
      customers: 36,
      collections: 130,
      avatar: 'KD',
      rank: 8
    },
    {
      id: 'EMP009',
      name: 'Rohan Gupta',
      branch: 'MUM001',
      branchName: 'Mumbai - Andheri',
      region: 'west',
      target: 470000,
      collected: 442000,
      achievement: 94,
      customers: 37,
      collections: 125,
      avatar: 'RG',
      rank: 9
    },
    {
      id: 'EMP010',
      name: 'Anjali Nair',
      branch: 'DEL001',
      branchName: 'Delhi - Connaught Place',
      region: 'north',
      target: 440000,
      collected: 405000,
      achievement: 92,
      customers: 33,
      collections: 118,
      avatar: 'AN',
      rank: 10
    }
  ];

  // Branch-wise Summary
  const branchSummary = [
    {
      branchId: 'BLR001',
      branchName: 'Bangalore - MG Road',
      region: 'south',
      staff: 2,
      target: 950000,
      collected: 990000,
      achievement: 104.2,
      customers: 87,
      collections: 304
    },
    {
      branchId: 'BLR002',
      branchName: 'Bangalore - Koramangala',
      region: 'south',
      staff: 2,
      target: 820000,
      collected: 808000,
      achievement: 98.5,
      customers: 75,
      collections: 263
    },
    {
      branchId: 'MUM001',
      branchName: 'Mumbai - Andheri',
      region: 'west',
      staff: 2,
      target: 950000,
      collected: 927000,
      achievement: 97.6,
      customers: 75,
      collections: 267
    },
    {
      branchId: 'MUM002',
      branchName: 'Mumbai - Bandra',
      region: 'west',
      staff: 1,
      target: 460000,
      collected: 445000,
      achievement: 96.7,
      customers: 39,
      collections: 140
    },
    {
      branchId: 'DEL001',
      branchName: 'Delhi - Connaught Place',
      region: 'north',
      staff: 2,
      target: 950000,
      collected: 907000,
      achievement: 95.5,
      customers: 77,
      collections: 276
    },
    {
      branchId: 'DEL002',
      branchName: 'Delhi - Dwarka',
      region: 'north',
      staff: 1,
      target: 430000,
      collected: 410000,
      achievement: 95.3,
      customers: 36,
      collections: 130
    }
  ];

  // Filter staff based on selection
  const getFilteredStaff = () => {
    let filtered = [...staffPerformance];

    if (selectedRegion !== 'all') {
      filtered = filtered.filter(staff => staff.region === selectedRegion);
    }

    if (selectedBranch !== 'all') {
      filtered = filtered.filter(staff => staff.branch === selectedBranch);
    }

    return filtered.sort((a, b) => b.achievement - a.achievement);
  };

  const getFilteredBranches = () => {
    let filtered = [...branchSummary];

    if (selectedRegion !== 'all') {
      filtered = filtered.filter(branch => branch.region === selectedRegion);
    }

    return filtered.sort((a, b) => b.achievement - a.achievement);
  };

  const getAchievementColor = (achievement) => {
    if (achievement >= 100) return 'success.main';
    if (achievement >= 90) return 'warning.main';
    return 'error.main';
  };

  const getProgressColor = (achievement) => {
    if (achievement >= 100) return 'success';
    if (achievement >= 90) return 'warning';
    return 'error';
  };

  const getMedalIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const filteredStaff = getFilteredStaff();
  const filteredBranches = getFilteredBranches();

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: isMobile ? 2 : 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
              Staff Performance
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Track and analyze collection team performance
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton sx={{ bgcolor: 'white', border: '1px solid #e0e0e0' }}>
              <Refresh />
            </IconButton>
            <IconButton sx={{ bgcolor: 'white', border: '1px solid #e0e0e0' }}>
              <Download />
            </IconButton>
          </Box>
        </Box>

        {/* Filters */}
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Period</InputLabel>
                  <Select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    label="Period"
                    sx={{ borderRadius: 1.5 }}
                  >
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="month">This Month</MenuItem>
                    <MenuItem value="quarter">This Quarter</MenuItem>
                    <MenuItem value="year">This Year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={selectedRegion}
                    onChange={(e) => {
                      setSelectedRegion(e.target.value);
                      setSelectedBranch('all');
                    }}
                    label="Region"
                    sx={{ borderRadius: 1.5 }}
                  >
                    {regions.map(region => (
                      <MenuItem key={region.id} value={region.id}>
                        {region.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Branch</InputLabel>
                  <Select
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    label="Branch"
                    sx={{ borderRadius: 1.5 }}
                  >
                    <MenuItem value="all">All Branches</MenuItem>
                    {branches
                      .filter(b => b.id !== 'all' && (selectedRegion === 'all' || b.region === selectedRegion))
                      .map(branch => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ bgcolor: '#e3f2fd', borderRadius: 2, p: 1.5, mr: 2 }}>
                    <Person sx={{ color: '#1976d2', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Staff
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {filteredStaff.length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 2, p: 1.5, mr: 2 }}>
                    <TrendingUp sx={{ color: '#2e7d32', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Target
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      ₹{(filteredStaff.reduce((sum, s) => sum + s.target, 0) / 100000).toFixed(1)}L
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ bgcolor: '#f3e5f5', borderRadius: 2, p: 1.5, mr: 2 }}>
                    <EmojiEvents sx={{ color: '#7b1fa2', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Collected
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      ₹{(filteredStaff.reduce((sum, s) => sum + s.collected, 0) / 100000).toFixed(1)}L
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ bgcolor: '#fff3e0', borderRadius: 2, p: 1.5, mr: 2 }}>
                    <TrendingUp sx={{ color: '#f57c00', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Avg Achievement
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {(filteredStaff.reduce((sum, s) => sum + s.achievement, 0) / filteredStaff.length).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={selectedTab} 
            onChange={(e, v) => setSelectedTab(v)}
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem'
              }
            }}
          >
            <Tab label="Leaderboard" />
            <Tab label="Branch-wise Report" />
          </Tabs>
        </Box>

        {/* Leaderboard Tab */}
        {selectedTab === 0 && (
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, py: 2 }}>Rank</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Staff Member</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Target</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Collected</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Achievement</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Collections</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStaff.map((staff, index) => (
                      <TableRow key={staff.id} hover>
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 30 }}>
                              {index + 1}
                            </Typography>
                            {getMedalIcon(index + 1) && (
                              <Typography variant="h5">{getMedalIcon(index + 1)}</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 700 }}>
                              {staff.avatar}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {staff.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {staff.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {staff.branchName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            ₹{(staff.target / 1000).toFixed(0)}K
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: getAchievementColor(staff.achievement) }}>
                            ₹{(staff.collected / 1000).toFixed(0)}K
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ minWidth: 150 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {staff.achievement}%
                              </Typography>
                              {staff.achievement >= 100 ? (
                                <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                              ) : (
                                <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                              )}
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(staff.achievement, 100)}
                              color={getProgressColor(staff.achievement)}
                              sx={{ height: 8, borderRadius: 1 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={staff.collections}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Branch-wise Report Tab */}
        {selectedTab === 1 && (
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, py: 2 }}>Rank</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Staff</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Target</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Collected</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Achievement</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Collections</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBranches.map((branch, index) => (
                      <TableRow key={branch.branchId} hover>
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 30 }}>
                              {index + 1}
                            </Typography>
                            {getMedalIcon(index + 1) && (
                              <Typography variant="h5">{getMedalIcon(index + 1)}</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {branch.branchName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {branch.branchId}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={`${branch.staff} Staff`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            ₹{(branch.target / 100000).toFixed(1)}L
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: getAchievementColor(branch.achievement) }}>
                            ₹{(branch.collected / 100000).toFixed(1)}L
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ minWidth: 150 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {branch.achievement}%
                              </Typography>
                              {branch.achievement >= 100 ? (
                                <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                              ) : (
                                <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                              )}
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(branch.achievement, 100)}
                              color={getProgressColor(branch.achievement)}
                              sx={{ height: 8, borderRadius: 1 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={branch.collections}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default StaffPerformance;
